import { Flex, GapSize } from "@/components/atoms/flex";
import {
	useCallback,
	useEffect,
	useRef,
	useState,
	MouseEvent,
	useMemo,
} from "react";
import axios, { HttpStatusCode } from "axios";
import { Button, Spinner } from "@fluentui/react-components";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
	ConversationMessages,
	EmailAction,
	Message,
	SendMessageData,
} from "@/repository/models/message";
import {
	MessagesKey,
	fetchConversationMessages,
	createForward,
	send,
	createReply,
	createReplyAll,
	updateDraft,
	deleteDraft,
	resumeInteraction,
	ConversationSummaryKey,
} from "@/repository/queryFns";
import { base64ToFile } from "@/common/helpers";
import { useEmailInteraction } from "./hook";
import { MessageInteractionProps } from "./types";
import {
	ConversationEmailEditor,
	EmailData,
	UploadStatus,
} from "@/components/molecules/emailEditor";
import { NewMessageArrivedInConversationNotification } from "@/common/models/emailNotification";
import { notificationListener } from "@/hooks/notification";
import { ServerEvent } from "@/common/notification";
import { useMessage } from "@/common/messages";
import { useIntl } from "react-intl";
import {
	AppCacheState,
	AppCacheActions,
	CacheKey,
	useAppCacheStore,
} from "@/common/stores/persistingStores/app";
import { useShallow } from "zustand/react/shallow";
import { EmailThreadSortOrder } from "@/components/molecules/email";
import {
	useCurrentInteractionStore,
	SendType,
} from "@/common/stores/nonpersistingStores/currentInteraction";
import { useDebounce } from "@/common/helpers/useDebounce";
import { isValidEmail } from "@/common/helpers/validationHelper";
import { addBreadcrumb, captureError } from "@/common/helpers/sentryHelper";
import { useTextMonitoring } from "@/hooks/supervisor";
import {
	ArrowForward20Regular,
	ArrowReply20Regular,
	ArrowReplyAll20Regular,
} from "@fluentui/react-icons";
import { useEmailLoadingMethods, useScrollEmailConversationToBottom } from "@/hooks/emails";
import { EmailConversation, EmailConversationHeader } from "@/components/molecules/emailConversation";
import { AgentInteractions, InteractionDirection } from "@/repository/models";
import { fetchInteractionsFn, interactionsQueryKey } from "@/repository/queryFns/interactions";
import { useEmailAttachments } from "@/components/molecules/emailEditor/useEmailAttachments";
import {
	useQueuesStore,
	QueuesState,
} from "@/common/stores/nonpersistingStores/queues";
import {
	SUBJECT_CHANGE_INFO_MESSAGE_KEY,
	Validations,
} from "@/common/constants";
import { useEmailAttachmentLimits } from "@/components/molecules/emailEditor/useEmailAttachmentLimits";
import { StorageKeys } from "@/common/constants/storageKeys";
import { Lockr } from "@/common/Lockr";
import { useEmailDraftSavingStore } from "@/common/stores/nonpersistingStores/emailDraftSaving";
import { calculateAttachmentsSize } from "@/common/helpers/emailHelper";

type DraftCreationProgressLocation = "top" | "bottom";

export const EmailInteractionContent = (props: MessageInteractionProps) => {
	const { sessionId, entryPointEmail, queueId, isPreview } = props;
	const intl = useIntl();
	const methods = useEmailInteraction();

	const { totalMB: emailMaxTotalAttachmentSizeMB, totalBytes: emailMaxTotalAttachmentSizeBytes } = useEmailAttachmentLimits();
	const { show: showMessage, hide: hideMessage } = useMessage();
	const [mailData, setMailData] = useState<EmailData>();
	const [isDraftCreationInProgress, setIsDraftCreationInProgress] =
		useState(false);
	const [
		showDraftCreationProgressLocation,
		setShowDraftCreationProgressLocation,
	] = useState<DraftCreationProgressLocation>();
	const [emailIsSending, setEmailIsSending] = useState<boolean>();
	const mailDataRef = useRef(mailData);
	const { cache: appStoreCache, setCache } = useAppCacheStore(
		useShallow((state: AppCacheState & AppCacheActions) => ({
			cache: state.cache,
			setCache: state.setCache,
		}))
	);
	const sortOrder: EmailThreadSortOrder = appStoreCache[CacheKey.EmailThreadSortOrder] ?? "oldest-first";
	const currentInteractionState = useCurrentInteractionStore();
	const queryClient = useQueryClient();
	const queues = useQueuesStore((state: QueuesState) => state.queues);

	const deletedAttachmentsRef = useRef<any>([]);
	const uploadedAttachmentsRef = useRef<string[]>([]);
	const hadNonInlineAttachmentDeleted = useRef(false);

	const lastProcessedDataRef = useRef<string>("");

	const [isTotalSizeExceeded, setIsTotalSizeExceeded] =
		useState<boolean>(false);

	const MAX_SIZE_EXCEEDED_MESSAGE_KEY = "maxTotalSizeExceededError";
	const isTextMonitoring = useTextMonitoring();

	const lastAppliedDataTimestamp = useRef<number>(0);

	const changeContextRef = useRef<{
		isMainContentChanged: boolean;
		draftNeedsUpdate: boolean;
		attachmentsChanged: boolean;
		callingMethod: string;
	}>({
		isMainContentChanged: false,
		draftNeedsUpdate: false,
		attachmentsChanged: false,
		callingMethod: "",
	});

	const messageQuery = {
		queryFn: ({ signal }: { signal: AbortSignal }) =>
			fetchConversationMessages(sessionId, isPreview, signal),
		queryKey: [MessagesKey, sessionId, isPreview],
		gcTime: 0,
		staleTime: 0,
		refetchOnMount: "always" as const,
		retry: (failureCount: number, error: Error) => {
			if (axios.isCancel(error)) {
				return false;
			}

			return failureCount < 3;
		},
		onError: (error: Error) => {
			if (axios.isCancel(error)) {
				return;
			}

			captureError(error, "Error loading email conversation messages", {
				interactionId: sessionId,
			});

			showMessage("workspace", {
				text: intl.formatMessage(
					{
						id: "ErrorMessage.EmailContentError",
					},
					{ interactionId: sessionId }
				),
				intent: "error",
			});
		},
	};

	const messagesResult = useQuery(messageQuery);

	const agentIsMonitoringTheInteraction = useMemo(() => {
		return (
			currentInteractionState.active?.monitoringSupervisorId ==
			Lockr.get(StorageKeys.UserId)
		);
	}, [currentInteractionState.active]);

	useEffect(() => {
		mailDataRef.current = mailData;
	}, [mailData]);

	useEffect(() => {
		return () => {

			if (isClosing(sessionId)) {
                cancelSaveDraft();
                clearClosing(sessionId);

                return;
            }

			if (!mailDataRef.current) {
				return;
			}

			if (!isPreview && !agentIsMonitoringTheInteraction) {
				if (mailData) {
					saveDraftChanges(mailData, true);
				} else {
					saveDraftChanges(mailDataRef.current, true);
				}
			}

			setMailData(undefined);

			hideMessage("workspace", SUBJECT_CHANGE_INFO_MESSAGE_KEY);

			if (deletedAttachmentsRef.current.length > 0 || hadNonInlineAttachmentDeleted.current) {
				console.log("removeQueries");
                queryClient.removeQueries({
                    queryKey: [MessagesKey, sessionId, isPreview],
                });
            }
			
		};
	}, [isPreview, agentIsMonitoringTheInteraction]);

	useEffect(() => {
		 const isRefetchedData =
            messagesResult.dataUpdatedAt > lastAppliedDataTimestamp.current;

        if (mailData && !agentIsMonitoringTheInteraction && !isRefetchedData) {
            return;
        }

		const draftMessage = (messagesResult.data?.messages || []).find(
			(x) => x.isDraft
		);

		if (!draftMessage || draftMessage.id === sentDraftIdRef.current) {
			setMailData(undefined);
			lastAppliedDataTimestamp.current = messagesResult.dataUpdatedAt;
			return;
		}

		changeContextRef.current = {
			...changeContextRef.current,
			isMainContentChanged: true,
			draftNeedsUpdate: true,
			attachmentsChanged: true,
		};

		setMailData(mapMessageToEmailData(draftMessage));

		if (emailIsSending) {
			setEmailIsSending(false);
		}
	}, [messagesResult.data, agentIsMonitoringTheInteraction]);

	useEffect(() => {
		const isSubjectChangedByLastMessage =
			messagesResult.data?.isSubjectChangedByLastMessage;

		if (isSubjectChangedByLastMessage) {
			showMessage("workspace", {
				key: SUBJECT_CHANGE_INFO_MESSAGE_KEY,
				text: intl.formatMessage({
					id: "Interaction.EmailEditor.SubjectChangedByLastMessage",
				}),
				intent: "info",
				isPermanent: true,
				bodyStyle: {
					textWrap: "wrap",
					paddingTop: "8px",
					paddingBottom: "8px",
				},
			});
		} else {
			hideMessage("workspace", SUBJECT_CHANGE_INFO_MESSAGE_KEY);
		}
	}, [messagesResult.data]);

	const isSubjectEditingEnabled = useMemo(() => {
		return (
			queues.find((x) => x.id == queueId)?.isEmailSubjectEditEnabled ?? false
		);
	}, [queues, queueId]);

	const mapMessageToEmailData = (draftMessage: Message) => {
		const email = new EmailData();
		email.refMessage = draftMessage;
		email.to =
			draftMessage.toRecipients?.map((x) => ({
				...x,
				isValid: isValidEmail(x.address),
			})) ?? [];
		email.bcc =
			draftMessage.bccRecipients?.map((x) => ({
				...x,
				isValid: isValidEmail(x.address),
			})) ?? [];
		email.cc =
			draftMessage.ccRecipients?.map((x) => ({
				...x,
				isValid: isValidEmail(x.address),
			})) ?? [];
		email.subject = draftMessage.subject || "";

		email.attachments = draftMessage.attachments.map((x) => ({
			id: x.id,
			file: base64ToFile(x.contentBytes, x.contentType, x.name),
			contentId: x.contentId,
			uploadStatus: UploadStatus.None,
		}));

		email.body = methods.replaceContentIdWithSource(
			draftMessage.body?.content ?? "",
			draftMessage.attachments
		);

		email.hasAiContent = draftMessage.hasAiContent ?? false;

		return email;
	};

	useEffect(() => {
		if (
			!mailData ||
			!messagesResult.data ||
			!!messagesResult.data.messages.filter((x) => !x.isDraft).length ||
			!currentInteractionState.active
		) {
			return;
		}
		const firstAgent = mailData.to[0] ?? mailData.cc[0] ?? mailData.bcc[0];
		const active = currentInteractionState.active;
		const unknownLabel = intl.formatMessage({ id: "Common.Unknown" });
		const description = mailData.subject;
		const contactInformation = firstAgent?.address ?? unknownLabel;
		const name = firstAgent?.name ?? firstAgent?.address ?? unknownLabel;

		if (
			description === active.description &&
			contactInformation === active.contactInformation &&
			name === active.name
		) {
			return;
		}

		currentInteractionState.showActive(
			{
				...active,
				description: description,
				contactInformation: contactInformation,
				name: name,
			},
			true
		);
	}, [mailData, messagesResult.data, currentInteractionState.active]);

	notificationListener<NewMessageArrivedInConversationNotification>(
		ServerEvent.NewMessageArrivedInConversation,
		(newMessagesNotification: NewMessageArrivedInConversationNotification) => {
			const currentInteractionId = appStoreCache[CacheKey.CurrentInteractionId];

			if (newMessagesNotification.sessionId == currentInteractionId) {
				showMessage("workspace", {
					intent: "info",
					text: intl.formatMessage({
						id: "Messaging.NewMessagesArrivedInConversation",
					}),
				});
			}

			if (currentInteractionState.active?.isOnHold) {
				resumeInteraction(currentInteractionId);
			}

			queryClient.invalidateQueries({
				queryKey: [MessagesKey, newMessagesNotification.sessionId, isPreview],
			});

			queryClient.invalidateQueries({
				queryKey: [ConversationSummaryKey, newMessagesNotification.sessionId],
			});
		}
	);

	notificationListener<NewMessageArrivedInConversationNotification>(
		ServerEvent.SupervisedEmailConversationChanged,
		(message: any) => {
			setTimeout(() => {
				queryClient.invalidateQueries({
					queryKey: [MessagesKey, message.sessionId, isPreview],
				});
			}, 2000);
		}
	);

	const onActionHandler = useCallback(
		(message: Message, type: EmailAction) => {
			const sender = {
				...message.sender,
				isValid: isValidEmail(message.sender.address),
			};
			const data = new EmailData();
			data.type = type;
			data.refMessage = message;

			setIsDraftCreationInProgress(true);

			if (type === EmailAction.Reply) {
				if (
					sender.address?.toLowerCase() === entryPointEmail?.toLowerCase() &&
					!!message.toRecipients.length
				) {
					data.to = message.toRecipients.map((x) => ({
						...x,
						isValid: isValidEmail(x.address),
					}));
				} else {
					data.to = [sender];
				}
			}

			if (type === EmailAction.ReplyAll) {
				const toRecipients = message.toRecipients.map((x) => ({
					...x,
					isValid: isValidEmail(x.address),
				}));
				const ccRecipients = message.ccRecipients.map((x) => ({
					...x,
					isValid: isValidEmail(x.address),
				}));

				if (
					sender.address?.toLowerCase() === entryPointEmail?.toLowerCase() &&
					!!message.toRecipients.length
				) {
					data.to = toRecipients.slice(0, 1);
					data.cc = [...toRecipients.slice(1), ...ccRecipients];
				} else {
					data.to = [{ ...sender, isValid: isValidEmail(sender.address) }];
					data.cc = [...toRecipients, ...ccRecipients];
				}
			}

			if (entryPointEmail) {
				data.to = data.to.filter(
					(x) => x.address?.toLowerCase() != entryPointEmail?.toLowerCase()
				);
				data.cc = data.cc.filter(
					(x) => x.address?.toLowerCase() != entryPointEmail?.toLowerCase()
				);
			}

			const replyActionsMap: Partial<{ [key in EmailAction]: any }> = {
				[EmailAction.Reply]: createReply,
				[EmailAction.ReplyAll]: createReplyAll,
				[EmailAction.Forward]: createForward,
			};

			const createDraftAction = replyActionsMap[type];

			createDraftAction(sessionId, data.refMessage?.id ?? "", data)
				.then((draftData: Message) => {
					setMailData(undefined);

					queryClient.setQueryData(
						[MessagesKey, sessionId, isPreview],
						(oldData?: ConversationMessages) => {
							if (!oldData) {
								return {
									messages: [draftData],
									isSubjectChangedByLastMessage: false,
									draftParentMessageId: message.id,
									emailAction: type,
								};
							}
							return {
								...oldData,
								messages: [...oldData.messages, draftData],
								draftParentMessageId: message.id,
								emailAction: type,
							};
						}
					);
				})
				.catch((ex: Error) => {
					captureError(ex, "Error while creating draft", {
						interactionId: sessionId,
					});
					console.error("Error while creating the draft: ", ex);
					showMessage("workspace", {
						text: intl.formatMessage({
							id: "Interaction.EmailEditor.DraftCreationError",
						}),
						intent: "error",
					});
				})
				.finally(() => {
					setIsDraftCreationInProgress(false);
				});
		},
		[sessionId, isPreview]
	);

	const onAnswerHandler = useCallback(
		(e: MouseEvent<HTMLButtonElement>, type: EmailAction, message: Message) => {
			e.stopPropagation();
			onActionHandler(message, type);
		},
		[onActionHandler]
	);

	const iconsMap = {
		[EmailAction.Reply]: <ArrowReply20Regular />,
		[EmailAction.ReplyAll]: <ArrowReplyAll20Regular />,
		[EmailAction.Forward]: <ArrowForward20Regular />,
	};

	const shouldShowProgress = useCallback(
		(progressLocation: DraftCreationProgressLocation) => {
			return (
				isDraftCreationInProgress &&
				progressLocation === showDraftCreationProgressLocation
			);
		},
		[showDraftCreationProgressLocation, isDraftCreationInProgress]
	);

	const buildAnswerButton = useCallback(
		(
			type: EmailAction,
			message: Message,
			appearance: any,
			insertText: boolean = false
		) => {
			const title = intl.formatMessage({
				id: `Interaction.EmailEditor.${EmailAction[type]}`,
			});
			const children = insertText ? title : undefined;

			return (
				<Button
					title={title}
					appearance={appearance}
					icon={iconsMap[type]}
					onClick={(e: MouseEvent<HTMLButtonElement>) => {
						setShowDraftCreationProgressLocation(insertText ? "bottom" : "top");
						onAnswerHandler(e, type, message);
					}}
					disabled={currentInteractionState.active?.isOnHold || isDraftCreationInProgress}
					children={children}
				/>
			);
		},
		[onAnswerHandler, intl, currentInteractionState.active, isDraftCreationInProgress]
	);

	const headerActions = useCallback(
		(message: Message, isLastMessage: boolean) => (
			<>
				{!mailData && !isTextMonitoring && (
					<Flex vAlign="center">
						{buildAnswerButton(EmailAction.Reply, message, "transparent")}
						{buildAnswerButton(EmailAction.ReplyAll, message, "transparent")}
						{buildAnswerButton(EmailAction.Forward, message, "transparent")}
						{isLastMessage && shouldShowProgress("top") && (
							<Spinner size="tiny" />
						)}
					</Flex>
				)}
			</>
		),
		[
			buildAnswerButton,
			!!mailData,
			currentInteractionState.active,
			shouldShowProgress,
		]
	);

	const footerActions = useCallback(
		(message: Message, isLastMessage: boolean) => {
			return (
				<>
					{!mailData && !isTextMonitoring && (
						<Flex gap={GapSize.Large}>
							{buildAnswerButton(EmailAction.Reply, message, undefined, true)}
							{buildAnswerButton(EmailAction.Forward, message, undefined, true)}
							{isLastMessage && shouldShowProgress("bottom") && (
								<Spinner size="tiny" />
							)}
						</Flex>
					)}
				</>
			);
		},
		[currentInteractionState.active, mailData, shouldShowProgress]
	);

	const highlightedMessageId = useMemo(() => {
		if (currentInteractionState.active?.isOnHold) {
			return undefined;
		}
		return messagesResult.data?.draftParentMessageId;
	}, [
		messagesResult.data?.draftParentMessageId,
		currentInteractionState.active?.isOnHold,
	]);

	const conversationScrollRef = useRef<HTMLDivElement>(null);

	const emailLoadingMethods = useEmailLoadingMethods(
		sessionId,
		false,
		messagesResult.data?.messages,
		false,
		headerActions,
		footerActions,
		isPreview,
		highlightedMessageId,
		sortOrder
	);

	useScrollEmailConversationToBottom(conversationScrollRef, sortOrder, emailLoadingMethods.emails.length);

	const getEmailSendMessageBySendType = (sentType: SendType) => {
		switch (sentType) {
			case SendType.Send:
				return "Interaction.EmailEditor.SuccessEmailSent";
			case SendType.SendAndPark:
				return "Interaction.EmailEditor.SuccessEmailSentAndPark";
			case SendType.SendAndClose:
				return "Interaction.EmailEditor.SuccessEmailSentAndClosed";
		}
	};

	const saveDraftChanges = useCallback(
		async (
			mailDataObj: EmailData | undefined,
			updateCache: boolean = false
		) => {
			if (!mailDataObj?.refMessage?.id) {
				return;
			}

			addBreadcrumb("ui_interaction", "Saving draft changes", mailDataObj);

			const { formattedBody } = await methods.processInlineImages(mailDataObj);

			const data: SendMessageData = {
				subject: mailDataObj?.subject?.trim(),
				body: formattedBody,
				cc: mailDataObj?.cc,
				bcc: mailDataObj?.bcc,
				to: mailDataObj?.to,
				attachments: [],
				hasAiContent: mailDataObj?.hasAiContent,
			};

			const abortController = new AbortController();
			draftAbortControllerRef.current = abortController;

			updateDraft(sessionId, mailDataObj.refMessage.id, data, abortController.signal)
				.then((message) => {
					if (!updateCache) {
						return;
					}

					queryClient.setQueryData(
						[MessagesKey, sessionId, isPreview],
						(oldData?: ConversationMessages) => {
							if (!oldData) {
								return {
									messages: [],
								};
							}

							return {
								...oldData,
								messages: oldData.messages?.map((m) =>
									m.id === message.id
										? { ...message, attachments: m.attachments }
										: m
								),
							};
						}
					);
				})
				.catch((err) => {
					if (axios.isCancel(err)) {
						return;
					}
					console.error("error updating draft", err);
					captureError(
						err,
						"Error occured when updating draft",
						undefined,
						mailDataObj
					);
				});
		},
		[sessionId, mailData]
	);

	const draftAbortControllerRef = useRef<AbortController | null>(null);
	const sentDraftIdRef = useRef<string | null>(null);

	const { register, unregister, isClosing, clearClosing } = useEmailDraftSavingStore();

	const [saveDraftChangesDebounced, cancelSaveDraft] = useDebounce(
		saveDraftChanges,
		4000
	);

	const cancelSaveDraftRef = useRef(cancelSaveDraft);
    cancelSaveDraftRef.current = cancelSaveDraft;

	useEffect(() => {
        register(sessionId, () => cancelSaveDraftRef.current());

        return () => {
            unregister(sessionId);
        };
    }, [sessionId]);

	// Subscribe to the interactions cache without fetching (enabled: false) — we only need to react to it
	// changing. When another interaction closes (e.g., a call ending on customer-left), the interactions
	// update re-renders/refetches the workspace, which would otherwise reload this email from the server and
	// drop keystrokes typed within the debounce window. Eagerly flushing the draft first preserves them.
	const { data: interactionsSnapshot } = useQuery<AgentInteractions>({
		queryKey: [interactionsQueryKey],
		queryFn: fetchInteractionsFn,
		enabled: false,
	});

	const skipInitialInteractionsFlush = useRef(true);

	useEffect(() => {
		// Skip the first run (initial cache read / this email's own load): nothing new to persist yet.
		if (skipInitialInteractionsFlush.current) {
			skipInitialInteractionsFlush.current = false;

			return;
		}

		// Mirror the unmount-save guards: don't resurrect a draft for a closing interaction, and skip
		// preview / monitoring where the agent isn't authoring.
		if (isClosing(sessionId) || isPreview || agentIsMonitoringTheInteraction) {
			return;
		}

		if (!mailDataRef.current) {
			return;
		}

		// updateCache: false — persist to the server only. Passing true would write the saved draft back into
		// the messages cache, re-firing the apply effect and resetting the editor (setMailData), which makes the
		// in-progress text visibly blink out. A silent background save leaves the editor (and the user's text and
		// cursor) untouched while the draft is safely persisted.
		saveDraftChanges(mailDataRef.current, false);
	}, [interactionsSnapshot]);

	const getSendErrorMessage = useCallback(
		(statusCode?: number) => {
			switch (statusCode) {
				case HttpStatusCode.PayloadTooLarge:
					return `${intl.formatMessage({ id: "Interaction.EmailEditor.ErrorExchangeLimitExceededSend" })} ${intl.formatMessage({ id: "Interaction.EmailEditor.ErrorExchangeLimitExceededBody" })}`;
				case HttpStatusCode.Gone:
					return intl.formatMessage({ id: "Interaction.EmailEditor.EmailNotFound" });
				default:
					return intl.formatMessage({ id: "Interaction.EmailEditor.ErrorEmailSent" });
			}
		},
		[intl]
	);

	const onSendClickHandler = useCallback(
		async (sendType: SendType) => {
			if (!mailData) {
				return;
			}
			if (
				mailData.to.some((x) => !x.isValid) ||
				mailData.bcc.some((x) => !x.isValid) ||
				mailData.cc.some((x) => !x.isValid)
			) {
				showMessage("workspace", {
					text: intl.formatMessage({
						id: "Interaction.EmailEditor.InvalidRecipients",
					}),
					intent: "error",
				});
				return;
			}

			if (!mailData.subject) {
				showMessage("workspace", {
					text: intl.formatMessage({
						id: "Interaction.EmailEditor.EmptySubject",
					}),
					intent: "error",
				});
				return;	
			}

			if (mailData.subject.trim().length > Validations.EmailSubjectMaxLength) {
				showMessage("workspace", {
					text: intl.formatMessage(
						{
							id: "Interaction.EmailEditor.SubjectMaxLengthExceeded",
						},
						{ maxLength: Validations.EmailSubjectMaxLength }
					),
					intent: "error",
				});
				return;
			}

			const totalSizeIsExceeded = checkAttachmentSize(mailData);

			if (totalSizeIsExceeded) {
				return;
			}

			addBreadcrumb("ui_interaction", "Sending email", {
				sendType,
				...mailData,
			});

			const { formattedBody } = await methods.processInlineImages(mailData);

			const data = {
				subject: mailData.subject?.trim(),
				body: formattedBody,
				cc: mailData.cc,
				bcc: mailData.bcc,
				to: mailData.to,
				attachments: [],
				sendType: sendType,
			};

			setEmailIsSending(true);
			sentDraftIdRef.current = mailData.refMessage?.id ?? null;

			cancelSaveDraft();
			draftAbortControllerRef.current?.abort();

			send(sessionId, mailData.refMessage?.id ?? "", data)
				.then(() => {
					sentDraftIdRef.current = null;
					setMailData(undefined);
					uploadedAttachmentsRef.current = [];
					deletedAttachmentsRef.current = [];
					hadNonInlineAttachmentDeleted.current = false;

					showMessage("workspace", {
						text: intl.formatMessage({
							id: getEmailSendMessageBySendType(sendType),
						}),
						intent: "success",
					});
					setTimeout(() => {
						queryClient.invalidateQueries({
							queryKey: [MessagesKey, sessionId, isPreview],
						});
						queryClient.invalidateQueries({
							queryKey: [ConversationSummaryKey, sessionId, isPreview],
						});
					}, 4000);
				})
				.catch((ex: Error) => {
					sentDraftIdRef.current = null;
					setEmailIsSending(false);
					captureError(ex, "Error while sending email", {
						interactionId: sessionId,
					});
					console.error("Error while sending the email: ", ex);
					const statusCode = axios.isAxiosError(ex) ? ex.response?.status : undefined;
					showMessage("workspace", {
						text: getSendErrorMessage(statusCode),
						intent: "error",
						bodyStyle: {
					        textWrap: "wrap",
					        paddingTop: "8px",
					        paddingBottom: "8px",
				        },
					});
				});
		},
		[mailData, cancelSaveDraft, isPreview, currentInteractionState.active, getSendErrorMessage]
	);

	const checkAttachmentSize = (emailData: EmailData) => {
		const totalSize = calculateAttachmentsSize(emailData?.attachments);

		if (totalSize > emailMaxTotalAttachmentSizeBytes) {
			showMessage("workspace", {
				key: MAX_SIZE_EXCEEDED_MESSAGE_KEY,
				text: intl.formatMessage(
					{
						id: "Interaction.EmailEditor.TotalAttachmentsSizeExceeded",
					},
					{ maxSize: emailMaxTotalAttachmentSizeMB }
				),
				intent: "error",
				isPermanent: true,
			});
			setIsTotalSizeExceeded(true);

			return true;
		} else {
			hideMessage("workspace", MAX_SIZE_EXCEEDED_MESSAGE_KEY);
			setIsTotalSizeExceeded(false);

			return false;
		}
	};

	const onChange = useCallback(
		async (
			newMailData?: EmailData,
			isMainContentChanged: boolean = false,
			draftNeedsUpdate: boolean = false,
			callingMethod: string = "",
			attachmentsChanged: boolean = false
		) => {
			if (!newMailData || isPreview) {
				return;
			}

			const dataHash = JSON.stringify({
				body: newMailData.body,
				attachments: newMailData.attachments.map((a) => ({
					id: a.id,
					contentId: a.contentId,
					name: a.file?.name,
					uploadStatus: a.uploadStatus,
				})),
				to: newMailData.to,
				cc: newMailData.cc,
				bcc: newMailData.bcc,
				subject: newMailData.subject,
				isMainContentChanged,
				draftNeedsUpdate,
			});

			const operationKey = `${dataHash}-${isMainContentChanged}-${callingMethod}`;

			if (lastProcessedDataRef.current === operationKey) {
				console.log(`Skipping duplicate onChange call from ${callingMethod}`);

				return;
			}

			lastProcessedDataRef.current = operationKey;

			changeContextRef.current = {
				isMainContentChanged,
				draftNeedsUpdate,
				attachmentsChanged,
				callingMethod,
			};

			setMailData(newMailData);
		},
		[setMailData, methods]
	);

	useEffect(() => {
		const handleUpdates = async () => {
			if (!mailData || agentIsMonitoringTheInteraction) {
				return;
			}

			const { isMainContentChanged, draftNeedsUpdate, attachmentsChanged } =
				changeContextRef.current;

			changeContextRef.current = {
				isMainContentChanged: false,
				draftNeedsUpdate: false,
				attachmentsChanged: false,
				callingMethod: "",
			};

			if (isMainContentChanged) {
				const { attachmentsToUpload, attachmentsToRemove } =
					await methods.prepareDraftAttachments(mailData);

				if (attachmentsToUpload.length > 0) {
					const filteredAttachments = attachmentsToUpload.filter(
						(attachment) => {
							const attachmentKey = attachment.contentId || attachment.name;
							return !uploadedAttachmentsRef.current.includes(attachmentKey);
						}
					);

					if (filteredAttachments.length > 0) {
						filteredAttachments.forEach((attachment) => {
							const attachmentKey = attachment.contentId || attachment.name;
							uploadedAttachmentsRef.current.push(attachmentKey);
						});

						uploadDraftInlineAttachments(filteredAttachments);
					}
				}

				if (attachmentsToRemove.length > 0) {
					attachmentsToRemove.forEach(async (image) => {
						if (!deletedAttachmentsRef.current.includes(image.id)) {
							deletedAttachmentsRef.current.push(image.id);
							const attachmentKey = image.contentId || image.file.name;
							uploadedAttachmentsRef.current =
								uploadedAttachmentsRef.current.filter(
									(key) => key !== attachmentKey
								);
							deleteDraftAttachment(image);
						}
					});
					saveDraftChanges(mailData);
				}
			}

			if (attachmentsChanged) {
				checkAttachmentSize(mailData);
			}

			if (draftNeedsUpdate) {
				saveDraftChangesDebounced(mailData);
			}
		};

		handleUpdates();
	}, [mailData, agentIsMonitoringTheInteraction]);

	const { uploadDraftInlineAttachments, deleteDraftAttachment } =
		useEmailAttachments(mailData, onChange, sessionId ?? "");

	const closeEditor = useCallback(() => {
		const messageId = mailData?.refMessage?.id;

		if (!messageId) {
			return;
		}

		addBreadcrumb("ui_interaction", "Closing email editor", { messageId });
		cancelSaveDraft();
		deleteDraft(sessionId, messageId)
			.then(() => {
				queryClient.setQueryData(
					[MessagesKey, sessionId, isPreview],
					(oldData?: ConversationMessages) => {
						if (!oldData) {
							return {
								messages: [],
								draftParentMessageId: undefined,
							};
						}

						return {
							...oldData,
							messages: oldData.messages?.filter((od) => od.id !== messageId),
							draftParentMessageId: undefined,
						};
					}
				);

				setMailData(undefined);
				uploadedAttachmentsRef.current = [];
				deletedAttachmentsRef.current = [];
				hadNonInlineAttachmentDeleted.current = false;
			})
			.catch((err: Error) => {
				showMessage("workspace", {
					text: intl.formatMessage({
						id: "Interaction.EmailEditor.DraftDeleteError",
					}),
					intent: "error",
				});
				captureError(err, "Error while deleting draft", {
					interactionId: sessionId,
					draftMessageId: messageId,
				});
				console.error("error deleting draft", err);
			});
	}, [mailData, cancelSaveDraft, currentInteractionState.active]);

	const onAttachmentDeleted = useCallback(() => {
        hadNonInlineAttachmentDeleted.current = true;
    }, []);

	const isOutboundConversationStart = useMemo(() => {
		const isOutbound =
			currentInteractionState.active?.direction ===
			InteractionDirection.Outbound;
		const hasNonDraft =
			messagesResult.data?.messages?.some((m) => !m.isDraft) ?? false;

		return isOutbound && !hasNonDraft;
	}, [messagesResult.data, currentInteractionState.active?.direction]);

	const spinner = useCallback(
		(label?: string) => (
			<Flex column vAlign="center" hAlign="center" style={{ padding: "32px" }}>
				<Spinner size="medium" label={label} labelPosition={"below"} />
			</Flex>
		),
		[]
	);

	const editor = useMemo(
		() =>
			!mailData ? (
				<></>
			) : (
				<ConversationEmailEditor
					data={mailData}
					onChange={onChange}
					onSendClick={onSendClickHandler}
					closeEditor={closeEditor}
					isSubjectEditingEnabled={isSubjectEditingEnabled}
					isOutboundConversationStart={isOutboundConversationStart}
					sessionId={sessionId}
					isSendDisabled={isTotalSizeExceeded}
					isFirstEmailInConversation={
						messagesResult.data?.messages?.length === 1
					}
					onAttachmentDeleted={onAttachmentDeleted}
					draftParentMessageId={messagesResult.data?.draftParentMessageId}
					emailAction={messagesResult.data?.emailAction}
				/>
			),
		[
			mailData,
			sessionId,
			onChange,
			onSendClickHandler,
			messagesResult,
			closeEditor,
			isOutboundConversationStart,
		]
	);

	const editorOrSpinner = useMemo(
		() =>
			emailIsSending
				? spinner(
						intl.formatMessage({ id: "Interaction.EmailEditor.EmailIsSending" })
					)
				: editor,
		[
			emailIsSending,
			mailData,
			sessionId,
			onChange,
			onSendClickHandler,
			messagesResult,
			closeEditor,
			isOutboundConversationStart,
		]
	);

	const isDataLoadingSpinnerVisible = useMemo(() => {

		 if (messagesResult.isLoading) {
            return true;
        }

        if (messagesResult.isRefetching && !messagesResult.data?.messages?.length) {
            return true;
        }

        return false;
	}, [
		messagesResult.isLoading,
		messagesResult.isRefetching,
		messagesResult.data,
	]);

	return (
		<>
			{!isDataLoadingSpinnerVisible && (
				<EmailConversation
					scrollRef={conversationScrollRef}
					header={
						<EmailConversationHeader
							messages={messagesResult.data?.messages}
							sessionId={sessionId}
							isHistory={false}
							isPreview={isPreview ?? false}
							sortOrder={sortOrder}
							onSortChange={(order) => setCache(CacheKey.EmailThreadSortOrder, order)}
							draftSubject={mailData?.subject}
						/>
					}
					emails={emailLoadingMethods.emails}
					editorAfterMessageId={
						messagesResult.data?.draftParentMessageId ??
						mailData?.refMessage?.id
					}
					editor={
						!!mailData && !currentInteractionState.active?.isOnHold ? (
							editorOrSpinner
						) : (
							<></>
						)
					}
				></EmailConversation>
			)}
			{isDataLoadingSpinnerVisible && spinner()}
		</>
	);
};
